import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useSlate } from 'slate-react';

import { useOutsideDetect } from '@mint-lib/hooks';
import { Editor, Range } from 'slate';

import { CustomSlateNode } from '../../common/TextArea/utils.jsx';
import Box from '../Box/Box.jsx';
import Select from '../Select/Select.jsx';
import { TextStylesList } from './TextAreaWithControls.constants.jsx';

type Props = {
  isDisabled: boolean;
  setFontSize?: Dispatch<SetStateAction<string>>;
  selectTextStyle?: (value: string) => void;
  readOnly?: boolean;
};

const TextStyleSelect = ({
  isDisabled,
  setFontSize = () => {},
  selectTextStyle = () => {},
  readOnly,
}: Props) => {
  const editor = useSlate();
  const [showMenu, setShowMenu] = useState(false);
  const [selectValue, setSelectValue] = useState<string | undefined>(undefined);

  // Update style based on selection
  useEffect(() => {
    const { selection } = editor;
    // Reset to placeholder if no selection
    if (!selection) {
      setSelectValue(undefined);
      return;
    }

    try {
      // Get the current block at selection start
      const [block] =
        Editor.nodes(editor, {
          at: Range.start(selection),
          match: (n) => !Editor.isEditor(n) && Editor.isBlock(editor, n),
        }) || [];

      // If no block found or block has no type, show placeholder
      if (!block || !(block[0] as CustomSlateNode).type) {
        setSelectValue(undefined);
        return;
      }

      // Get block type and show placeholder if it's empty or not a valid style
      const [node] = block;
      const blockType = (node as CustomSlateNode).type;
      const isValidStyle = TextStylesList.some(
        (style) => style.value === blockType,
      );

      setSelectValue(isValidStyle ? blockType : undefined);
    } catch (error) {
      console.error('Error getting block style:', error);
      setSelectValue(undefined);
    }
  }, [editor.selection, editor]);

  const handleChange = (value: string) => {
    if (value === 'style_heading1') {
      setFontSize('30px');
    }
    if (value === 'style_heading2') {
      setFontSize('20px');
    }
    if (value === 'style_normal') {
      setFontSize('16px');
    }

    setSelectValue(value);
    selectTextStyle(value);
  };

  const dropDownRef = useRef(null);

  useOutsideDetect(dropDownRef, showMenu, () => setShowMenu(false));

  return (
    <Box role="textStyleSelect" width="146px">
      <Select
        disabled={isDisabled}
        size="small"
        fullWidth
        width="fit-content"
        value={selectValue || ''}
        placeholder="Text Style"
        onChange={handleChange}
        options={TextStylesList}
        data-testid="test-select"
        readOnly={readOnly}
      />
    </Box>
  );
};

export default TextStyleSelect;
